import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../../auth/models/User';
import { AuthenticationStore } from '../../permission/services/authentication.store';
import { LogService } from '../../shared/log/log.service';
import { PropertyLookupStore } from '../../shared/lookup/property-lookup/property-lookup.store';
import { PropertyLookupDetailQuery } from '../../shared/models/property-lookup-detail-query';
import { OnyxEvent } from '../models/event';
import { Party } from '../models/party';
import { PartyType } from '../models/party-type';
import { Vendor } from '../models/vendor';
import { EventStore } from '../service/event.store';
import { PropertyLookupConverter } from '../utils/property-lookup-converter';
import { VendorPopupComponent } from '../vendor-popup/vendor-popup.component';
import { EventValidationService } from '../service/event-validation.service';
import { VendorCardDetailComponent } from '../vendor-card-detail/vendor-card-detail.component';
import { take } from 'rxjs/operators';
import { TaxDeterminationStore } from '../service/tax-determination.store';
import { TaxRequest } from '../models/TaxRequest';
import { GlobalTaxResponse, TaxResponse } from '../models/TaxResponse';
import { List } from 'immutable';

@Component({
  selector: 'pioneer-vendor-lookup-form',
  templateUrl: 'vendor-lookup-form.component.html',
  styleUrls: [ './vendor-lookup-form.component.css']
})
export class VendorLookupFormComponent implements OnInit {

  @Input() parentForm: FormGroup;
  @Input() isReadOnly = false;
  @Input() isNewEvent = false;
  @Input() inputEmail: string;

  @Output() outputEmail: EventEmitter<string> = new EventEmitter<string>();
  @Output() removeVendorProcess: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(VendorPopupComponent) propertyPopup: VendorPopupComponent;
  @ViewChildren('card') cards: QueryList<VendorCardDetailComponent>;

  partyType = PartyType.PROPERTY;
  onyxEvent: OnyxEvent;
  addVendorButtonTittle = '';
  isPropertySelected = false;
  canAddVendor = false;
  showAdvancedPropertySearch: boolean;
  openedCardIndex = 0;
  isReadyOnlyProperty: boolean;

  private checkErrorVendorsNum = false;

  constructor(private translateService: TranslateService,
              private propertyLookupStore: PropertyLookupStore,
              private logService: LogService,
              private authenticationStore: AuthenticationStore,
              private taxDeterminationStore: TaxDeterminationStore,
              private eventStore: EventStore,
              private eventValidationService: EventValidationService) {
  }

  ngOnInit() {
    this.isPropertySelected = false;

    this.eventStore.selectedEvent.pipe(
      take(1)
    ).subscribe((event: OnyxEvent) => {
      if (!event) {
        this.onyxEvent = new OnyxEvent();
      } else if (event instanceof OnyxEvent) {
        this.onyxEvent = event;
      } else {
        this.onyxEvent = new OnyxEvent(event);
      }

      if (this.onyxEvent.eventProperty && this.onyxEvent.eventProperty.eventPartyId) {
        this.isPropertySelected = true;
      }
    });

    if (this.authenticationStore.isSinglePropertyUser() && this.isNewEvent) {
      this.authenticationStore.user.pipe(
        take(1)
      ).subscribe((user: User) => {
        this.getPropertyDetailFromUser(user);
      });
    }

    this.showAdvancedPropertySearch = this.authenticationStore.hasPermission('ViewExtendedPropertySearch');

    this.eventValidationService.canAddVendor().pipe(
      take(1)
    ).subscribe((canAddVendor: boolean) => {
      this.canAddVendor = canAddVendor;
    });

    this.isReadyOnlyProperty = this.authenticationStore.isSinglePropertyUser();
  }

  private getPropertyDetailFromUser(user: User) {
    if (user && user.allChains && user.allChains.length > 0
      && user.allBrands && user.allBrands.length > 0
      && user.allProperties && user.allProperties.length > 0) {
      const propertyQuery = new PropertyLookupDetailQuery({
        chain: user.allChains[0],
        brand: user.allBrands[0],
        propertyId: user.allProperties[0],
      });

      this.isPropertySelected = true;

      this.logService.debug(`propertySummary ${JSON.stringify(propertyQuery)}`);
      this.propertyLookupStore.getDetail(propertyQuery).pipe(
        take(1)
      ).subscribe();
    }
  }

  isPropertyMissing(): boolean {
    const hasVendors = this.onyxEvent && this.onyxEvent.eventVendors && this.onyxEvent.eventVendors.length > 0;
    return this.checkErrorVendorsNum && !hasVendors;
  }

  closedPropertyPopup(vendor: Vendor) {
    if (vendor) {
      // consult tax rate
      let taxResponseList: List<TaxResponse> = null;
      let taxRequest: TaxRequest = PropertyLookupConverter.createTaxRequest(vendor, this.parentForm.value.eventAgency);
      this.taxDeterminationStore.verifyTaxRatesByParty(taxRequest)
        .subscribe(async (listTaxResponse: GlobalTaxResponse) => {
          taxResponseList = listTaxResponse.isValidTax && listTaxResponse.taxResponses ? listTaxResponse.taxResponses : null;

          const eventProperty = PropertyLookupConverter.vendorToParty(vendor, true, taxResponseList);
          const eventPropertyStringified = JSON.stringify(eventProperty);
          this.logService.info(eventPropertyStringified);

          if (!this.onyxEvent.eventVendors) {
            this.onyxEvent.eventVendors = [];
          }
          const vendorsCount = this.onyxEvent.eventVendors.push(JSON.parse(eventPropertyStringified));

          // Open the vendor card just added
          const lastCard = this.cards ? this.cards.last : undefined;
          if (lastCard !== undefined) {
            this.onToggle(vendorsCount - 1, lastCard);
          }
          this.isPropertySelected = true;
          this.checkErrorVendorsNum = false;
        });
    }
  }

  openAdvancedPropertySearchPopup() {
    this.propertyPopup.open();
  }

  setupPropertyEmail(email: string): void {
    this.outputEmail.emit(email);
  }

  onDeleteVendor(vendor: Party) {
    this.onyxEvent.eventVendors.forEach((item, index) => {
      if (item === vendor) {
        this.deleteVendorForm(vendor);
        this.onyxEvent.eventVendors.splice(index, 1);
        this.removeVendorProcess.emit(vendor);
        if (!this.onyxEvent.eventVendors || this.onyxEvent.eventVendors.length === 0) {
          this.checkErrorVendorsNum = true;
        }

        if (index === this.openedCardIndex) {
          this.openedCardIndex = -1;
        }
      }
    });
    this.eventStore.setEvent(this.onyxEvent);
  }

  deleteVendorForm(vendor: Party) {
    const vendorCode = vendor.code ? vendor.code : vendor.chain + '|' + vendor.brand + '|' + vendor.propertyId;
    const vendorFormName = `eventVendor-${vendorCode}`;
    this.parentForm.removeControl(vendorFormName);
  }

  public isValidRequiredInformation(): boolean {
    if (!this.parentForm.get('event').valid) {
      this.addVendorButtonTittle = this.translateService.instant('EVENT.PROPERTY.ADD_NEW_VENDOR_BUTTON_DISABLED_TOOLTIP');
    } else {
      this.addVendorButtonTittle = '';
    }
    return this.parentForm.get('event').valid;
  }

  onToggle(index: number, card: VendorCardDetailComponent) {
    if (this.openedCardIndex === index) {
      // Toggling the card that was open. By setting the index to -1, all cards will be closed.
      this.openedCardIndex = -1;
    } else {
      // Open the vendor card in the provided index.
      this.openedCardIndex = index;

      // Scroll to the card
      setTimeout(() => {
        card.cardWrapper.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }

  trackVendorByFn(index: number, vendor: Party) {
    return vendor.eventPartyId;
  }

  public shouldShowAddVendorButton(): boolean {
    const baseConditions = this.canAddVendor && !this.isReadOnly && this.showAdvancedPropertySearch;

    // Update mode
    if (this.onyxEvent && this.onyxEvent.eventId != null) {
      return baseConditions;
    }

    // New event mode
    const noVendors = !this.onyxEvent || !this.onyxEvent.eventVendors || this.onyxEvent.eventVendors.length === 0;
    return baseConditions && noVendors;
  }

  public shouldShowAddVendorsMessage(): boolean {
      return this.onyxEvent &&
            this.onyxEvent.eventId == null && // Only for new events
            this.onyxEvent.eventVendors &&
            this.onyxEvent.eventVendors.length > 0;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/vendor-lookup-form/vendor-lookup-form.component.ts